import { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Html5Qrcode } from "html5-qrcode";
import { X } from "lucide-react";

interface QRScannerProps {
  open: boolean;
  onClose: () => void;
  onScan: (boxNumber: string) => void;
}

export const QRScanner = ({ open, onClose, onScan }: QRScannerProps) => {
  const [error, setError] = useState<string>("");
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const isScanning = useRef(false);

  useEffect(() => {
    if (open && !isScanning.current) {
      startScanner();
    }

    return () => {
      stopScanner();
    };
  }, [open]);

  const startScanner = async () => {
    try {
      isScanning.current = true;
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
        },
        (decodedText) => {
          // Extract box number from QR code
          // Assuming QR code contains just the box number or a URL with the box number
          let boxNumber = decodedText;
          
          // If it's a URL, try to extract the box number
          if (decodedText.includes('http')) {
            const match = decodedText.match(/box\/([^/?]+)/);
            if (match) {
              boxNumber = match[1];
            }
          }
          
          onScan(boxNumber);
          stopScanner();
          onClose();
        },
        (errorMessage) => {
          // Ignore scanning errors (they happen continuously)
        }
      );
    } catch (err) {
      setError("Unable to access camera. Please check permissions.");
      isScanning.current = false;
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current && isScanning.current) {
      try {
        await scannerRef.current.stop();
        scannerRef.current = null;
        isScanning.current = false;
      } catch (err) {
        console.error("Error stopping scanner:", err);
      }
    }
  };

  const handleClose = () => {
    stopScanner();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Scan QR Code Label</DialogTitle>
          <DialogDescription>
            Position the QR code label within the frame to scan
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          <div id="qr-reader" className="w-full rounded-lg overflow-hidden bg-black"></div>
          
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
              {error}
            </div>
          )}

          <Button variant="outline" className="w-full" onClick={handleClose}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
